import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Calendar, Check, ClipboardList, FileCheck2, RefreshCw, Search, Send, UserRoundCog, UsersRound, Workflow, X, CheckCircle2 } from 'lucide-react';
import { IMG } from '../mock';

// ================== CONFIG FORMULAIRE ==================
const WEB3FORMS_ACCESS_KEY = 'c98d7adb-6e49-4ef4-aef3-ab94bb396534';
const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const CALENDLY_URL = 'https://calendly.com/amane-mounia/coaching-decouverte-gratuit?month=2026-08';

const RH_NEEDS = [
  'Structurer mon organisation RH',
  'Clarifier les rôles et responsabilités',
  'Améliorer la gestion de mon équipe',
  'Mettre en place des processus RH',
  'Autre',
];

const issues = [
  ['Les missions ne sont pas clairement réparties', 'Plusieurs personnes font la même chose, certaines tâches sont oubliées, et les responsabilités deviennent difficiles à suivre.', ClipboardList],
  ['L’équipe manque de cadre ou de suivi', 'Les collaborateurs avancent sans méthode commune, sans rythme de contrôle et sans indicateurs simples pour mesurer la progression.', UsersRound],
  ['Le recrutement n’est pas relié à une vraie organisation', 'Recruter sans fiche de poste, sans objectifs et sans parcours d’intégration crée des erreurs coûteuses pour l’entreprise.', BarChart3],
];

const interventions = [
  ['ORGANISATION', 'Structuration des rôles et missions', 'Clarifier qui fait quoi, quelles sont les priorités et comment l’équipe doit fonctionner.', ['Répartition des responsabilités', 'Clarification des missions', 'Organisation par pôles', 'Lecture simple des besoins humains']],
  ['POSTES', 'Fiches de poste et parcours d’intégration', 'Formaliser les attentes pour mieux recruter, intégrer et suivre les collaborateurs.', ['Fiches de poste claires', 'Objectifs de prise de poste', 'Plan d’intégration', 'Suivi des premières semaines']],
  ['PILOTAGE', 'Suivi d’équipe et montée en compétence', 'Installer des habitudes de suivi simples pour aider les équipes à progresser.', ['Rythme de points d’équipe', 'Suivi des objectifs', 'Feedback constructif', 'Besoins en formation']],
];

const questions = [
  ['Qui est responsable de quoi ?', 'Identifier les responsabilités principales, les zones de flou et les tâches sans propriétaire clair.'],
  ['Quels sont les objectifs attendus ?', 'Définir les résultats attendus par poste, par mission ou par période de suivi.'],
  ['Quel profil faut-il recruter ?', 'Relier le recrutement à un besoin réel, une fiche de poste et un environnement concret.'],
  ['Comment suivre la progression ?', 'Mettre en place des points simples, des retours réguliers et des indicateurs lisibles.'],
];

const method = [
  ['Diagnostic de l’organisation actuelle', 'Analyse des rôles, des difficultés, des doublons, des tâches oubliées et des besoins prioritaires.'],
  ['Clarification des responsabilités', 'Répartition des missions, clarification des pôles et définition des responsabilités par personne ou par poste.'],
  ['Création des supports RH utiles', 'Fiches de poste, tableau de répartition, plan d’intégration, grille de suivi ou recommandations RH.'],
  ['Accompagnement à la mise en place', 'Présentation des recommandations, ajustements avec la direction et transmission des outils.'],
  ['Suivi et amélioration', 'Ajustements après utilisation, retours d’expérience et accompagnement de la montée en compétence.'],
];

const methodIcons = [Search, UserRoundCog, FileCheck2, Workflow, RefreshCw];

export default function AccompagnementRH() {
  const [showForm, setShowForm] = useState(false);

  return (
    <main className="pt-[72px] rh-service rh-support-page">
      <section className="rh-service-hero">
        <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1>Des équipes structurées. Des rôles clairs. Une organisation qui avance.</h1>
            <p>MCE vous accompagne pour structurer vos ressources humaines, clarifier les responsabilités et mettre en place une organisation efficace, durable et adaptée à votre croissance.</p>
            <button 
              type="button" 
              onClick={() => setShowForm(true)} 
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 mt-4 rounded-lg font-bold text-[14px] text-white bg-[#0F1E3D] hover:bg-[#0099CC] shadow-md hover:shadow-lg transition-all duration-200"
            >
              Faire le point sur mon organisation RH
              <ArrowRight className="w-4 h-4 text-[#FFB800]" />
            </button>
          </div>
          <img src="/assets/Accompagnement_RH.png" alt="Accompagnement des équipes et managers" />
        </div>
      </section>

      <section className="rh-diagnostic">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="rh-diagnostic-lead">
            <h2>Quand l’organisation interne devient floue, l’activité ralentit.</h2>
            <p>Le rôle de MCE est d’aider à remettre de la clarté : qui fait quoi, avec quels objectifs, selon quelles priorités et avec quel suivi.</p>
            {['Clarifier les responsabilités', 'Réduire les incompréhensions', 'Structurer les postes', 'Accompagner la montée en compétence'].map(x => (
              <b key={x}><Check />{x}</b>
            ))}
          </div>
          <div className="rh-diagnostic-issues">
            {issues.map(([t,d,I]) => (
              <article key={t}><I/><div><h3>{t}</h3><p>{d}</p></div></article>
            ))}
          </div>
        </div>
      </section>

      <section id="nos-interventions" className="rh-interventions">
        <div className="max-w-[1180px] mx-auto px-6">
          <RHHead title="Structurer l’essentiel."/>
          <div>
            {interventions.map(([,t,d,l]) => (
              <article key={t}><h3>{t}</h3><p>{d}</p><ul>{l.map(x => <li key={x}>{x}</li>)}</ul></article>
            ))}
          </div>
        </div>
      </section>

      <section className="rh-questions">
        <div className="max-w-[1120px] mx-auto px-6">
          <RHHead title="4 questions pour clarifier votre équipe."/>
          <div>
            {questions.map(([t,d],i) => (
              <article key={t}><span>{String(i+1).padStart(2,'0')}</span><h3>{t}</h3><p>{d}</p></article>
            ))}
          </div>
        </div>
      </section>

      <section className="rh-method-list">
        <div className="max-w-[1050px] mx-auto px-6">
          <RHHead title="5 étapes pour retrouver de la clarté." />
          <div>
            {method.map(([t,d],i) => { 
              const Icon = methodIcons[i]; 
              return (
                <article key={t}>
                  <span className="rh-method-marker"><Icon/></span>
                  <div><h3>{t}</h3><p>{d}</p></div>
                </article>
              ); 
            })}
          </div>
        </div>
      </section>

      {showForm && <RHDiagnosticModal onClose={() => setShowForm(false)} />}
    </main>
  );
}

function RHHead({tag,title,text}) { 
  return <header className="rh-head">{tag && <span>{tag}</span>}<h2>{title}</h2>{text && <p>{text}</p>}</header>; 
}

/* ============================================================
   MODALE : Formulaire de diagnostic RH
   ============================================================ */
function RHDiagnosticModal({ onClose }) {
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    need: '',
    description: '',
  });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

 // Exemple de remplacement de la fonction handleSubmit
const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');

  if (!form.name || !form.company || !form.email || !form.need) {
    setError('Merci de remplir les champs obligatoires.');
    return;
  }

  setSending(true);
  try {
    // Exemple d'envoi vers VOTRE propre API / Backend qui enregistre en BDD :
    const response = await fetch('/api/sauvegarder-diagnostic', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });

    if (!response.ok) throw new Error('Erreur lors de la sauvegarde');

    setSubmitted(true);
    setForm({ name: '', company: '', email: '', phone: '', need: '', description: '' });
    setTimeout(() => { setSubmitted(false); onClose(); }, 3500);
  } catch (err) {
    setError('Envoi impossible. Réessayez ou contactez-nous directement.');
  } finally {
    setSending(false);
  }
};

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" />

      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0A2540] text-white p-6 md:p-8 relative pr-12">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-6 right-6 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors text-white"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
          
          <h2 className="text-xl md:text-2xl font-bold tracking-tight">
            Faire le point sur votre organisation RH
          </h2>
          <p className="mt-2 text-[13px] md:text-[14px] text-gray-200 leading-relaxed font-normal">
            Quelques informations suffisent pour nous permettre de comprendre votre besoin. Notre équipe vous recontacte pour affiner votre demande.
          </p>
        </div>

        <div className="p-6 md:p-8 overflow-y-auto space-y-5">
          {submitted && (
            <div className="rounded-lg p-4 bg-green-50 border border-green-200 flex items-center gap-3 text-green-800 text-[13px] font-medium">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
              <span>Votre demande a bien été envoyée. MCE vous recontacte sous 48h.</span>
            </div>
          )}

          {error && (
            <div className="rounded-lg p-4 bg-red-50 border border-red-200 text-red-700 text-[13px] font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <input type="checkbox" name="botcheck" tabIndex="-1" autoComplete="off" className="hidden" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RHField 
                label="Nom de l'entreprise *" 
                value={form.company} 
                onChange={(v) => setForm({ ...form, company: v })} 
                required 
              />
              <RHField 
                label="Nom et prénom du contact *" 
                value={form.name} 
                onChange={(v) => setForm({ ...form, name: v })} 
                required 
              />
              <RHField 
                label="Téléphone / WhatsApp" 
                type="tel" 
                value={form.phone} 
                onChange={(v) => setForm({ ...form, phone: v.replace(/[^0-9+()\s-]/g, '') })} 
              />
              <RHField 
                label="E-mail professionnel *" 
                type="email" 
                value={form.email} 
                onChange={(v) => setForm({ ...form, email: v })} 
                required 
              />
            </div>

            <div className="space-y-2">
              <label className="block text-[13px] font-bold text-[#0F1E3D]">
                Votre besoin principal *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {RH_NEEDS.map((need) => (
                  <label
                    key={need}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all cursor-pointer text-[13px] ${
                      form.need === need
                        ? 'border-[#0A2540] bg-blue-50/40 font-medium text-[#0A2540]'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="need"
                      value={need}
                      checked={form.need === need}
                      onChange={() => setForm({ ...form, need })}
                      className="w-4 h-4 text-[#0A2540] focus:ring-0 cursor-pointer"
                    />
                    <span>{need}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-[13px] font-bold text-[#0F1E3D]">
                Décrivez brièvement votre besoin
              </label>
              <textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Précisez votre situation, vos attentes ou votre contexte d'équipe..."
                className="w-full rounded-xl bg-[#F8FAFC] border border-gray-200 px-3.5 py-2.5 text-[13.5px] text-gray-800 focus:bg-white focus:outline-none focus:border-[#0A2540] transition-colors resize-none"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                disabled={sending}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-bold text-[13.5px] text-white bg-[#0A2540] hover:bg-[#12365B] shadow-sm transition-all disabled:opacity-60 cursor-pointer"
              >
                <Send className="w-4 h-4" />
                {sending ? 'Envoi en cours…' : 'Envoyer ma demande'}
              </button>

              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-bold text-[13.5px] text-[#0A2540] bg-gray-100 hover:bg-gray-200 transition-all text-center"
              >
                <Calendar className="w-4 h-4" />
                Réserver un échange direct
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* Composant de Champ générique (Déclaré une seule fois) */
function RHField({ label, type = 'text', value, onChange, required }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[13px] font-bold text-[#0F1E3D]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-xl bg-[#F8FAFC] border border-gray-200 px-3.5 py-2.5 text-[13.5px] text-gray-800 focus:bg-white focus:outline-none focus:border-[#0A2540] transition-colors"
      />
    </div>
  );
}